import { Card } from "../../card";
import { NumericValue } from "../../numeric-value";
import type { IProps } from "./metric-box.types";
import { FunctionComponent, memo } from "react";

/** Default `renderValue` implementation, hoisted to module scope so it's a stable reference across renders. */
const defaultRenderValue: NonNullable<IProps["renderValue"]> = (value, otherProps) => (
    <NumericValue value={value} {...otherProps} />
);

/**
 * Display a metric value optionally versus a comparison value.
 * @param props.value           Value for the metric.
 * @param props.icon            Icon for the metric.
 * @param props.isLoading       If the data is loading.
 * @param props.menuOptions     Menu actions for metrics.
 * @param props.renderValue     Custom renderer for the value and comparison value. Defaults to rendering via
 *                              NumericValue.
 * @param props.title           Label/description for the metric.
 * @param props.tooltipContent  Explanation of the metric.
 * @param props.comparisonValue Value shown in the footer as a comparison against `value`. Ignored when `footer` is
 *                              provided.
 * @param props.footer          Custom footer content (e.g. a coloured comparison badge) that replaces the default
 *                              "vs {value} overall" text when provided.
 * @param props.color           Card accent color.
 * @param props["data-testid"]  Test id applied to the underlying Card.Metric.
 * @param props.options         Intl.NumberFormat options passed through to the value renderer.
 * @param props.unit            Unit suffix appended to the rendered value (e.g. "%").
 * @returns                     A metric icon, label, and values.
 */
const MetricBoxComponent: FunctionComponent<IProps> = ({
    comparisonValue,
    footer,
    icon,
    isLoading,
    menuOptions,
    renderValue = defaultRenderValue,
    title,
    tooltipContent,
    value,
    color,
    "data-testid": dataTestId,
    ...numericValueProps
}) => {
    if (isLoading) {
        return <Card.Metric.Skeleton />;
    }

    // Computed only when actually rendered, so a custom renderValue is never invoked for the loading skeleton or
    // when a custom footer replaces the default comparison footer.
    const buildDefaultFooter = () =>
        typeof comparisonValue === "undefined" || comparisonValue === null ? undefined : (
            <>vs {renderValue(comparisonValue, numericValueProps)} overall</>
        );

    return (
        <Card.Metric
            title={title}
            icon={icon}
            color={color}
            menu={menuOptions}
            metric={[
                {
                    value: typeof value === "number" ? renderValue(value, numericValueProps) : "--",
                    footer: typeof footer !== "undefined" ? footer : buildDefaultFooter(),
                },
            ]}
            data-testid={dataTestId}
        />
    );
};

const MetricBox = memo(MetricBoxComponent);

export { MetricBox };
